import { useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { routesConfig, getMenuRoutes, type RouteConfig } from "../../config/routes.config";

interface SidebarProps {
  isCollapsed: boolean;
}

const Sidebar = ({ isCollapsed }: SidebarProps) => {
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());

  // Obtener solo las rutas que deben mostrarse en el menú
  const menuItems = getMenuRoutes(routesConfig);

  // const oldMenuItems = [
  //   {
  //     id: "dashboard",
  //     label: "Dashboard",
  //     icon: <LayoutDashboard className="h-5 w-5" />,
  //     path: "/dashboard",
  //   },
  //   {
  //     id: "parametrizacion",
  //     label: "Parametrización",
  //     icon: <Settings className="h-5 w-5" />,
  //     submenu: [
  //       {
  //         id: "param-general",
  //         label: "General",
  //         icon: <Database className="h-4 w-4" />,
  //         path: "/parametrizacion/general",
  //       },
  //       {
  //         id: "param-usuarios",
  //         label: "Usuarios",
  //         icon: <Users className="h-4 w-4" />,
  //         path: "/parametrizacion/usuarios",
  //       },
  //       {
  //         id: "param-permisos",
  //         label: "Permisos",
  //         icon: <FileText className="h-4 w-4" />,
  //         submenu: [
  //           {
  //             id: "permisos-roles",
  //             label: "Roles",
  //             icon: <FileText className="h-3 w-3" />,
  //             path: "/parametrizacion/permisos/roles",
  //           },
  //           {
  //             id: "permisos-accesos",
  //             label: "Accesos",
  //             icon: <FileText className="h-3 w-3" />,
  //             path: "/parametrizacion/permisos/accesos",
  //           },
  //         ],
  //       },
  //     ],
  //   },
  //   {
  //     id: "inventario",
  //     label: "Inventario",
  //     icon: <Package className="h-5 w-5" />,
  //     submenu: [
  //       {
  //         id: "inventario-productos",
  //         label: "Productos",
  //         icon: <Package className="h-4 w-4" />,
  //         path: "/inventario/productos",
  //       },
  //       {
  //         id: "inventario-categorias",
  //         label: "Categorías",
  //         icon: <FileText className="h-4 w-4" />,
  //         path: "/inventario/categorias",
  //       },
  //       {
  //         id: "inventario-almacenes",
  //         label: "Almacenes",
  //         icon: <Database className="h-4 w-4" />,
  //         path: "/inventario/almacenes",
  //       },
  //     ],
  //   },
  //   {
  //     id: "ventas",
  //     label: "Ventas",
  //     icon: <ShoppingCart className="h-5 w-5" />,
  //     submenu: [
  //       {
  //         id: "ventas-pedidos",
  //         label: "Pedidos",
  //         icon: <ShoppingCart className="h-4 w-4" />,
  //         path: "/ventas/pedidos",
  //       },
  //       {
  //         id: "ventas-clientes",
  //         label: "Clientes",
  //         icon: <Users className="h-4 w-4" />,
  //         path: "/ventas/clientes",
  //       },
  //     ],
  //   },
  //   {
  //     id: "reportes",
  //     label: "Reportes",
  //     icon: <BarChart3 className="h-5 w-5" />,
  //     submenu: [
  //       {
  //         id: "reportes-ventas",
  //         label: "Ventas",
  //         icon: <BarChart3 className="h-4 w-4" />,
  //         path: "/reportes/ventas",
  //       },
  //       {
  //         id: "reportes-inventario",
  //         label: "Inventario",
  //         icon: <Package className="h-4 w-4" />,
  //         path: "/reportes/inventario",
  //       },
  //     ],
  //   },
  // ];

  const toggleSubmenu = (id: string) => {
    setOpenSubmenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const renderMenuItem = (item: RouteConfig, level: number = 0) => {
    const hasSubmenu = item.children && item.children.length > 0;
    const isOpen = openSubmenus.has(item.id);
    const paddingLeft = `${(level + 1) * 0.75}rem`;
    const Icon = item.icon;

    if (hasSubmenu) {
      return (
        <div key={item.id}>
          <button
            onClick={() => toggleSubmenu(item.id)}
            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-smooth hover:bg-muted/80 ${
              isOpen ? "bg-muted text-foreground" : "text-muted-foreground"
            } ${isCollapsed ? "justify-center" : ""}`}
            style={{ paddingLeft: isCollapsed ? "1rem" : paddingLeft }}
            title={isCollapsed ? item.label : ""}
          >
            <div className="flex items-center gap-3 min-w-0">
              {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
              {!isCollapsed && (
                <span className="truncate">{item.label}</span>
              )}
            </div>
            {!isCollapsed && (
              <span className="flex-shrink-0 ml-2">
                {isOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </span>
            )}
          </button>
          {!isCollapsed && isOpen && (
            <div className="bg-muted/30">
              {item.children!.map((subItem) => renderMenuItem(subItem, level + 1))}
            </div>
          )}
        </div>
      );
    }

    // Solo renderizar NavLink si la ruta tiene un componente asociado
    if (!item.component) return null;

    return (
      <NavLink
        key={item.id}
        to={item.path}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-smooth hover:bg-muted/80 ${
            isActive
              ? "bg-primary/10 text-primary border-r-2 border-primary"
              : "text-muted-foreground"
          } ${isCollapsed ? "justify-center" : ""}`
        }
        style={{ paddingLeft: isCollapsed ? "1rem" : paddingLeft }}
        title={isCollapsed ? item.label : ""}
      >
        {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
        {!isCollapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    );
  };

  return (
    <aside
      className={`bg-card border-r border-border h-full transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-64"
      } flex flex-col`}
    >
      {/* Header del Sidebar */}
      {!isCollapsed && (
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-foreground">
            Menú
          </h2>
        </div>
      )}

      {/* Menú Items */}
      <nav className="flex-1 overflow-y-auto py-4">
        {menuItems.map((item) => renderMenuItem(item))}
      </nav>

      {/* Footer del Sidebar */}
      {!isCollapsed && (
        <div className="p-4 border-t border-border text-xs text-muted-foreground text-center">
          v1.0.0
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
